import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  filteredLength: number;
  handlePageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  rowsPerPage,
  filteredLength,
  handlePageChange,
}) => {
  return (
    <div className="border border-t-0 rounded-b-xl border-gray-100 py-3 px-2 sm:py-4 sm:px-4 dark:border-white/[0.05] flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Showing Entries Info */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full sm:w-auto">
        <p className="pb-2 sm:pb-0 text-xs sm:text-sm font-medium text-center text-gray-500 border-b border-gray-100 dark:border-gray-800 dark:text-gray-400 sm:border-b-0 sm:text-left">
          Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
          {Math.min(currentPage * rowsPerPage, filteredLength)} of{" "}
          {filteredLength} entries
        </p>
      </div>

      {/* Pagination Buttons */}
      <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2 w-full sm:w-auto">
        {/* Previous Button */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-2 min-w-[40px] rounded text-xs sm:text-sm h-10 border font-medium flex items-center justify-center
            ${
              currentPage === 1
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400 hover:bg-blue-500/[0.08] hover:text-brand-500 dark:hover:text-brand-500"
            }`}
        >
          Previous
        </button>

        {/* Page Numbers with Ellipsis */}
        {currentPage > 3 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              className="px-3 py-2 min-w-[40px] rounded text-xs sm:text-sm h-10 border bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400"
            >
              1
            </button>
            <span className="text-gray-500">...</span>
          </>
        )}

        {Array.from({ length: totalPages }, (_, index) => index + 1)
          .slice(Math.max(0, currentPage - 2), currentPage + 1)
          .map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-2 min-w-[40px] rounded text-xs sm:text-sm h-10 border font-medium flex items-center justify-center
                ${
                  currentPage === page
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400 hover:bg-blue-500/[0.08] hover:text-brand-500 dark:hover:text-brand-500"
                }`}
            >
              {page}
            </button>
          ))}

        {currentPage < totalPages - 2 && (
          <>
            <span className="text-gray-500">...</span>
            <button
              onClick={() => handlePageChange(totalPages)}
              className="px-3 py-2 min-w-[40px] rounded text-xs sm:text-sm h-10 border bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400"
            >
              {totalPages}
            </button>
          </>
        )}

        {/* Next Button */}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-3 py-2 min-w-[40px] rounded text-xs sm:text-sm h-10 border font-medium flex items-center justify-center
            ${
              currentPage === totalPages
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400 hover:bg-blue-500/[0.08] hover:text-brand-500 dark:hover:text-brand-500"
            }`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;
